<template>
  <div class="min-h-screen flex flex-col bg-white">

    <div v-if="reloadOverlay || navOverlay" class="fixed inset-0 z-[99999] bg-white flex items-center justify-center">
      <div class="flex items-center gap-3 text-gray-700 font-semibold">
        <div class="h-5 w-5 rounded-full border-2 border-gray-300 border-t-blue-600 animate-spin" />
        <div>{{ $t('header.loading') || 'Loading...' }}</div>
      </div>
    </div>

    <ClientOnly>
      <div v-if="topbarText && !isInternational" class="bg-blue-600 text-white text-xs">
        <div class="max-w-7xl mx-auto px-6 py-2 flex items-center justify-center font-semibold">
          <a
            v-if="topbarLink"
            :href="topbarLink"
            target="_blank"
            rel="noopener noreferrer"
            class="hover:underline"
          >
            {{ topbarText }}
          </a>
          <span v-else>{{ topbarText }}</span>
        </div>
      </div>

    </ClientOnly>

    <!-- Trustpilot Bar -->
    <TrustpilotBar />

    <!-- Barra superior -->
    <div v-if="!isInternational" class="bg-[#0f172a] text-gray-300 text-[13px] min-h-[36px] h-[36px] px-6">
      <div class="max-w-7xl mx-auto h-full flex items-center justify-center gap-4 flex-wrap overflow-visible">
        <span class="flex items-center gap-1.5">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
          </svg>
          {{ $t('header.instantDelivery') }}
        </span>
        <span class="hidden sm:inline text-gray-500">|</span>
        <span class="flex items-center gap-1.5">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z"/>
          </svg>
          {{ $t('header.whatsappSupport') }}
        </span>
        <span class="hidden sm:inline text-gray-500">|</span>
        <span class="flex items-center gap-1.5">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.745 3.745 0 011.043 3.296c.63.677 1.593 1.8 1.593 3.068z"/>
          </svg>
          {{ $t('header.securePayment') }}
        </span>
        <span class="hidden sm:inline text-gray-500">|</span>
        <span class="flex items-center gap-1.5">
          <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
          </svg>
          {{ $t('header.customersServed') }}
        </span>
      </div>
    </div>

    <!-- HEADER -->
    <header class="border-b bg-white shadow-sm sticky top-0 z-40">
      <div class="max-w-7xl mx-auto px-6 py-2">
        <div class="min-h-[70px] md:min-h-[80px] flex items-center justify-between gap-4 py-2 md:py-0">
          <div class="flex items-center gap-4 min-w-0">
            <button
              type="button"
              class="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-lg border text-gray-700"
              @click="mobileMenuOpen = true"
              :aria-label="$t('header.openMenu')"
            >
              ☰
            </button>

            <NuxtLink to="/" class="flex items-center gap-3 min-w-0">
              <picture v-if="effectiveLogoPath">
                <source v-if="effectiveLogoWebpPath" :srcset="effectiveLogoWebpPath" type="image/webp" />
                <img :src="effectiveLogoPath" :alt="siteName" :style="isInternational ? 'height: 60px; max-height: 60px; width: auto; max-width: 200px; object-fit: contain; display: block;' : 'height: 55px; max-height: 60px; width: auto; max-width: 200px; object-fit: contain; display: block;'" />
              </picture>
            </NuxtLink>
          </div>

          <form class="hidden md:flex flex-1 max-w-2xl" @submit.prevent="submitSearch">
            <div class="flex w-full rounded-xl overflow-hidden border border-[#dbe3ef] focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 shadow-sm transition">
              <input
                v-model="search"
                type="search"
                :placeholder="t.searchPlaceholder"
                class="w-full h-12 bg-white px-4 text-sm outline-none placeholder-gray-400"
              />
              <button
                type="submit"
                class="h-12 px-5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm transition flex-shrink-0 flex items-center gap-2"
              >
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"/>
                </svg>
                <span class="hidden lg:inline">{{ t.searchButton }}</span>
              </button>
            </div>
          </form>

          <div class="flex items-center gap-1.5">
            <div class="flex items-center gap-1.5">

              <select
                v-if="isEnDomain"
                class="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs font-semibold text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                :value="intl.countryCode || 'AUTO'"
                aria-label="Country"
                @change="onCountryChange"
              >
                <option value="AUTO">AUTO</option>
                <option value="BR">BR</option>
                <option value="US">US</option>
                <option value="GB">UK</option>
                <option value="ES">ES</option>
                <option value="PT">PT</option>
                <option value="DE">DE</option>
                <option value="FR">FR</option>
                <option value="IT">IT</option>
              </select>

              <select
                v-if="isEnDomain"
                class="h-8 rounded-md border border-gray-300 bg-white px-2 text-xs font-semibold text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                :value="intl.currencyLower"
                aria-label="Currency"
                @change="onCurrencyChange"
              >
                <option value="usd">USD</option>
                <option value="eur">EUR</option>
                <option value="brl">BRL</option>
              </select>
            </div>

            <NuxtLink
              to="/account/login"
              class="hidden md:flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition px-3 py-1.5 rounded-lg"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"/>
              </svg>
              <span class="hidden lg:block">{{ t.myAccount }}</span>
            </NuxtLink>

            <NuxtLink
              to="/cart"
              class="relative inline-flex items-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 text-sm font-semibold transition shadow-sm"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.874-7.148a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z"/>
              </svg>
              <span class="hidden sm:block">{{ t.cart }}</span>
              <span
                v-if="cartCount > 0"
                class="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center"
              >
                {{ cartCount }}
              </span>
            </NuxtLink>
          </div>
        </div>

        <nav class="hidden md:flex items-center gap-1 h-9 border-t">
          <NuxtLink to="/" class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-sm font-semibold text-gray-600 hover:text-[#2563eb] hover:bg-[#eff6ff] transition">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"/>
            </svg>
            {{ t.home }}
          </NuxtLink>
          <NuxtLink
            v-for="it in mainMenu.filter(i => !i.isAffiliate)"
            :key="it.label"
            :to="it.to"
            class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] text-sm font-semibold text-gray-600 hover:text-[#2563eb] hover:bg-[#eff6ff] transition"
          >
            {{ it.label }}
          </NuxtLink>
          <NuxtLink
            v-for="it in mainMenu.filter(i => i.isAffiliate)"
            :key="it.label"
            :to="it.to"
            class="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-bold text-[#2563eb] bg-[#eff6ff] border border-[#2563eb] hover:bg-[#2563eb] hover:text-white transition"
          >
            {{ it.label }}
          </NuxtLink>
        </nav>
      </div>
    </header>

    <div v-if="mobileMenuOpen" class="fixed inset-0 z-50 md:hidden">
      <div class="absolute inset-0 bg-black/40" @click="mobileMenuOpen = false" />

      <div class="absolute inset-y-0 left-0 w-80 max-w-[92vw] bg-white shadow-2xl flex flex-col">
        <div class="px-4 py-3 border-b flex items-center justify-between">
          <NuxtLink to="/" class="flex items-center gap-2" @click="mobileMenuOpen = false">
            <picture>
              <source v-if="effectiveLogoWebpPath" :srcset="effectiveLogoWebpPath" type="image/webp" />
              <img :src="effectiveLogoPath" :alt="siteName" class="h-12 w-auto" />
            </picture>
            <span class="font-extrabold text-gray-900 truncate text-sm">{{ safeSiteName }}</span>
          </NuxtLink>
          <button
            type="button"
            class="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            @click="mobileMenuOpen = false"
            :aria-label="t.closeMenu"
          >
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <nav class="flex-1 overflow-y-auto px-3 py-3">
          <div v-if="isEnDomain" class="grid grid-cols-2 gap-2 mb-3">
            <select
              class="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm font-semibold text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              :value="intl.countryCode || 'AUTO'"
              aria-label="Country"
              @change="(e) => { onCountryChange(e); mobileMenuOpen = false }"
            >
              <option value="AUTO">AUTO</option>
              <option value="BR">BR</option>
              <option value="US">US</option>
              <option value="GB">UK</option>
              <option value="ES">ES</option>
              <option value="PT">PT</option>
              <option value="DE">DE</option>
              <option value="FR">FR</option>
              <option value="IT">IT</option>
            </select>
            <select
              class="h-9 rounded-lg border border-gray-300 bg-white px-2 text-sm font-semibold text-gray-700 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              :value="intl.currencyLower"
              aria-label="Currency"
              @change="(e) => { onCurrencyChange(e); mobileMenuOpen = false }"
            >
              <option value="usd">USD</option>
              <option value="eur">EUR</option>
              <option value="brl">BRL</option>
            </select>
          </div>

          <div class="space-y-1">
            <NuxtLink
              to="/"
              class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-gray-800 font-semibold hover:bg-blue-50 transition"
              @click="mobileMenuOpen = false"
            >
              <span class="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 text-sm">🏠</span>
              {{ t.home }}
            </NuxtLink>
            <NuxtLink
              v-for="it in mainMenu.filter(i => !i.isAffiliate)"
              :key="it.label"
              :to="it.to"
              class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-gray-800 font-semibold hover:bg-blue-50 transition"
              @click="mobileMenuOpen = false"
            >
              <span class="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center text-gray-600 text-sm">•</span>
              {{ it.label }}
            </NuxtLink>
            <NuxtLink
              v-for="it in mainMenu.filter(i => i.isAffiliate)"
              :key="it.label"
              :to="it.to"
              class="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-blue-600 font-semibold bg-blue-50 border border-blue-200 hover:bg-blue-100 transition"
              @click="mobileMenuOpen = false"
            >
              <span class="w-7 h-7 rounded-lg bg-blue-200 flex items-center justify-center text-blue-700 text-sm">🎯</span>
              {{ it.label }}
            </NuxtLink>
          </div>
        </nav>

        <div class="p-3 border-t">
          <NuxtLink
            to="/cart"
            class="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold px-4 py-2.5 transition"
            @click="mobileMenuOpen = false"
          >
            {{ $t('header.goToCart') }}
          </NuxtLink>
        </div>
      </div>
    </div>

    <!-- CONTEÚDO DAS PÁGINAS -->
    <main class="flex-1">
      <NuxtPage />
    </main>

    <!-- WHATSAPP BUTTON -->
    <ClientOnly>
      <WhatsAppButton />
    </ClientOnly>

    <!-- FOOTER -->
    <SiteFooterNew
      :site-name="siteName"
      :support-email="supportEmail"
      :whatsapp-href="whatsappHref"
      :whatsapp-label="whatsappLabel"
      :company-legal-name="isInternational ? siteName : companyLegalName"
      :company-cnpj="isInternational ? null : companyCnpj"
      :company-address="isInternational ? null : companyAddress"
      :company-phone="isInternational ? null : companyPhone"
      :company-email="isInternational ? 'support@gvgmall.co' : companyEmail"
      :paginas="footerInstitutionalLinks"
      :products-index-path="productsIndexPath"
      :blog-to="blogTo"
      :affiliate-to="affiliateTo"
      :affiliate-label="affiliateLabel"
      :about-us-path="aboutUsPath"
      :digital-delivery-path="digitalDeliveryPath"
      :refund-policy-path="refundPolicyPath"
      :privacy-policy-path="privacyPolicyPath"
      :terms-of-use-path="termsOfUsePath"
      :is-en-domain="isEnDomain"
      :footer-description="$t('footer.description') || 'Digital software licenses with instant delivery.'"
      :footer-links-title="$t('footer.links') || 'Links'"
    />

  </div>
</template>

<script setup lang="ts">
const {
  siteName,
  logoPath,
  supportEmail,
  topbarText,
  topbarLink,
  whatsappNumber,
  companyLegalName,
  companyCnpj,
  companyAddress,
  companyPhone,
  companyEmail
} = useSiteBranding()

const config = useRuntimeConfig()

const storeSlug = computed(() => String((config.public as any)?.storeSlug || '').trim())

const normalizedHost = computed(() => {
  if (import.meta.server) {
    try {
      const headers = useRequestHeaders(['x-forwarded-host', 'host']) as Record<string, string | undefined>
      const raw = headers?.['x-forwarded-host'] || headers?.host || ''
      const first = String(raw).split(',')[0]?.trim()
      return String(first || '').toLowerCase()
    } catch {
      return ''
    }
  }

  return String(window.location.host || '').toLowerCase()
})

const isInternational = useIsInternational()

const isEnDomain = computed(() => {
  const h = normalizedHost.value
  return h.endsWith('.store') || h.includes('casadosoftware.store')
})

const logoWebpPath = computed(() => {
  const raw = String(logoPath || '').trim()
  if (!raw) return ''
  if (raw.endsWith('.webp')) return raw
  return ''
})

const NATIONAL_LOGOS = ['/logo.png', '/logo-mercadosoftwares.png', '']

const casaSoftwareLogo = 'https://pub-388810139d004c3eb59d2d54c6e92aa7.r2.dev/uploads/Logo%20Marca%201.png'
const isCasaDoSoftware = computed(() => {
  if (normalizedHost.value.includes('casadosoftware.com.br')) return true
  return storeSlug.value === 'casadosoftware'
})

const effectiveLogoPath = computed(() => {
  // Use new 10 years trusted logo
  const newLogo = '/logo-gvgmall-10yrs.webp'
  if (isInternational.value) {
    const lp = String(logoPath || '').trim()
    return NATIONAL_LOGOS.includes(lp) ? newLogo : (lp || newLogo)
  }
  return String(logoPath || '').trim() || (isCasaDoSoftware.value ? casaSoftwareLogo : newLogo)
})

const isNationalLogoInIntl = computed(() =>
  isInternational.value && NATIONAL_LOGOS.includes(String(logoPath || '').trim())
)

const effectiveLogoWebpPath = computed(() => {
  const raw = String(effectiveLogoPath.value || '').trim()
  if (!raw) return ''
  if (raw.endsWith('.webp')) return raw
  return ''
})

const safeSiteName = computed(() => {
  const n = String(siteName || '').trim()
  return n || 'Digital Licenses'
})

const intl = useIntlContext()

// Language system as specified
const route = useRoute()

const currentLang = computed(() => {
  const lang = String(route.query.lang || 'en')
  return ['en', 'es', 'fr'].includes(lang) ? lang : 'en'
})

const translations = {
  en: {
    searchPlaceholder: 'Search products...',
    searchButton: 'Search',
    myAccount: 'My Account',
    cart: 'Cart',
    home: 'Home',
    windows: 'Windows',
    office: 'Office',
    windowsServer: 'Windows Server',
    adobe: 'Adobe',
    autodesk: 'Autodesk',
    games: 'Games',
    blog: 'Blog',
    support: 'Support',
    partnerProgram: 'Partner Program',
    genuineSoftwareLicenses: 'Genuine Software Licenses — Instant Delivery',
    originalSoftwareLicenses: 'Original Software Licenses',
    atTheBestPrice: 'at the Best Price',
    windowsOfficeAndMore: 'Windows, Office, and more — 100% genuine licenses delivered instantly to your email. Secure checkout with Stripe.',
    viewLicenses: 'View Licenses',
    howItWorks: 'How It Works',
    instantDelivery: 'Instant Delivery',
    licenseKeySent: 'License key sent to your email in minutes.',
    securePayment: 'Secure Payment',
    poweredByStripe: 'Powered by Stripe — no card data stored.',
    genuineLicenses: 'Genuine Licenses',
    originalFromOfficialSources: '100% original, direct from official sources.',
    globalSupport: 'Global Support',
    customerSupportWorldwide: 'Customer support available worldwide.',
    featuredLicenses: 'Featured Licenses',
    bestSellingSoftwareLicenses: 'Best-selling software licenses, ready for instant activation.',
    viewAll: 'View all →',
    buyNow: 'Buy Now',
    threeSimpleSteps: 'Three simple steps to activate your license.',
    chooseYourLicense: 'Choose Your License',
    browseCatalogSelect: 'Browse our catalog and select the software you need.',
    paySecurelyStripe: 'Pay Securely with Stripe',
    completeCheckout: 'Complete checkout with credit/debit card via Stripe. USD and EUR accepted.',
    receiveKeyEmail: 'Receive Your Key by Email',
    licenseKeyDelivered: 'Your license key is delivered instantly to your inbox. Activate and enjoy.',
    whyChooseUs: 'Why Choose Us',
    trustedByCustomers: 'Trusted by customers in over 50 countries.',
    emailDelivery: 'Email Delivery',
    licenseKeyArrives: 'Your license key arrives directly in your inbox, no waiting, no shipping.',
    stripePayments: 'Stripe Payments',
    paySafely: 'Pay safely with any major credit or debit card. USD and EUR supported.',
    genuine100: '100% Genuine',
    fullyAuthentic: 'All licenses are fully authentic and come with official activation support.',
    easyActivation: 'Easy Activation',
    simpleInstructions: 'Simple step-by-step instructions included with every license key.',
    customerSupport: 'Customer Support',
    teamReadyHelp: 'Our team is ready to help you with activation and any questions.',
    invoiceAvailable: 'Invoice Available',
    needReceipt: 'Need a receipt? An invoice can be provided upon request.',
    browseByCategory: 'Browse by Category',
    findRightLicense: 'Find the right license for your needs.',
    frequentlyAskedQuestions: 'Frequently Asked Questions',
    readyToGetLicense: 'Ready to get your license?',
    instantDeliverySecurePayment: 'Instant delivery. Secure payment. Genuine software.',
    browseLicenses: 'Browse Licenses',
    closeMenu: 'Close Menu'
  },
  es: {
    searchPlaceholder: 'Buscar productos...',
    searchButton: 'Buscar',
    myAccount: 'Mi Cuenta',
    cart: 'Carrito',
    home: 'Inicio',
    windows: 'Windows',
    office: 'Office',
    windowsServer: 'Windows Server',
    adobe: 'Adobe',
    autodesk: 'Autodesk',
    games: 'Juegos',
    blog: 'Blog',
    support: 'Soporte',
    partnerProgram: 'Programa de Socios',
    genuineSoftwareLicenses: 'Licencias de Software Originales — Entrega Instantánea',
    originalSoftwareLicenses: 'Licencias de Software Originales',
    atTheBestPrice: 'al Mejor Precio',
    windowsOfficeAndMore: 'Windows, Office y más — licencias 100% originales entregadas instantáneamente a tu email. Pago seguro con Stripe.',
    viewLicenses: 'Ver Licencias',
    howItWorks: 'Cómo Funciona',
    instantDelivery: 'Entrega Instantánea',
    licenseKeySent: 'Clave de licencia enviada a tu email en minutos.',
    securePayment: 'Pago Seguro',
    poweredByStripe: 'Impulsado por Stripe — no se almacenan datos de tarjeta.',
    genuineLicenses: 'Licencias Originales',
    originalFromOfficialSources: '100% originales, directamente de fuentes oficiales.',
    globalSupport: 'Soporte Global',
    customerSupportWorldwide: 'Soporte al cliente disponible en todo el mundo.',
    featuredLicenses: 'Licencias Destacadas',
    bestSellingSoftwareLicenses: 'Licencias de software más vendidas, listas para activación instantánea.',
    viewAll: 'Ver todo →',
    buyNow: 'Comprar Ahora',
    threeSimpleSteps: 'Tres simples pasos para activar tu licencia.',
    chooseYourLicense: 'Elige Tu Licencia',
    browseCatalogSelect: 'Navega nuestro catálogo y selecciona el software que necesitas.',
    paySecurelyStripe: 'Paga Seguro con Stripe',
    completeCheckout: 'Completa el checkout con tarjeta de crédito/débito vía Stripe. USD y EUR aceptados.',
    receiveKeyEmail: 'Recibe Tu Clave por Email',
    licenseKeyDelivered: 'Tu clave de licencia se entrega instantáneamente en tu bandeja de entrada. Activa y disfruta.',
    whyChooseUs: 'Por Qué Elegirnos',
    trustedByCustomers: 'Confiado por clientes en más de 50 países.',
    emailDelivery: 'Entrega por Email',
    licenseKeyArrives: 'Tu clave de licencia llega directamente a tu bandeja de entrada, sin esperas, sin envíos.',
    stripePayments: 'Pagos Stripe',
    paySafely: 'Paga seguro con cualquier tarjeta de crédito o débito importante. USD y EUR soportados.',
    genuine100: '100% Original',
    fullyAuthentic: 'Todas las licencias son completamente auténticas y vienen con soporte de activación oficial.',
    easyActivation: 'Activación Fácil',
    simpleInstructions: 'Instrucciones simples paso a paso incluidas con cada clave de licencia.',
    customerSupport: 'Soporte al Cliente',
    teamReadyHelp: 'Nuestro equipo está listo para ayudarte con la activación y cualquier pregunta.',
    invoiceAvailable: 'Factura Disponible',
    needReceipt: '¿Necesitas un recibo? Se puede proporcionar una factura bajo solicitud.',
    browseByCategory: 'Navegar por Categoría',
    findRightLicense: 'Encuentra la licencia adecuada para tus necesidades.',
    frequentlyAskedQuestions: 'Preguntas Frecuentes',
    readyToGetLicense: '¿Listo para obtener tu licencia?',
    instantDeliverySecurePayment: 'Entrega instantánea. Pago seguro. Software original.',
    browseLicenses: 'Navegar Licencias',
    closeMenu: 'Cerrar Menú'
  },
  fr: {
    searchPlaceholder: 'Rechercher des produits...',
    searchButton: 'Rechercher',
    myAccount: 'Mon Compte',
    cart: 'Panier',
    home: 'Accueil',
    windows: 'Windows',
    office: 'Office',
    windowsServer: 'Windows Server',
    adobe: 'Adobe',
    autodesk: 'Autodesk',
    games: 'Jeux',
    blog: 'Blog',
    support: 'Support',
    partnerProgram: 'Programme Partenaire',
    genuineSoftwareLicenses: 'Licences de Logiciels Authentiques — Livraison Instantanée',
    originalSoftwareLicenses: 'Licences de Logiciels Originaux',
    atTheBestPrice: 'au Meilleur Prix',
    windowsOfficeAndMore: 'Windows, Office et plus — licences 100% authentiques livrées instantanément à votre email. Paiement sécurisé avec Stripe.',
    viewLicenses: 'Voir les Licences',
    howItWorks: 'Comment Ça Marche',
    instantDelivery: 'Livraison Instantanée',
    licenseKeySent: 'Clé de licence envoyée à votre email en minutes.',
    securePayment: 'Paiement Sécurisé',
    poweredByStripe: 'Alimenté par Stripe — aucune donnée de carte stockée.',
    genuineLicenses: 'Licences Authentiques',
    originalFromOfficialSources: '100% originales, directement des sources officielles.',
    globalSupport: 'Support Mondial',
    customerSupportWorldwide: 'Support client disponible dans le monde entier.',
    featuredLicenses: 'Licences en Vedette',
    bestSellingSoftwareLicenses: 'Licences de logiciels les plus vendus, prêtes pour activation instantanée.',
    viewAll: 'Voir tout →',
    buyNow: 'Acheter Maintenant',
    threeSimpleSteps: 'Trois étapes simples pour activer votre licence.',
    chooseYourLicense: 'Choisissez Votre Licence',
    browseCatalogSelect: 'Parcourez notre catalogue et sélectionnez le logiciel dont vous avez besoin.',
    paySecurelyStripe: 'Payez en Toute Sécurité avec Stripe',
    completeCheckout: 'Finalisez l\'achat avec carte de crédit/débit via Stripe. USD et EUR acceptés.',
    receiveKeyEmail: 'Recevez Votre Clé par Email',
    licenseKeyDelivered: 'Votre clé de licence est livrée instantanément dans votre boîte de réception. Activez et profitez.',
    whyChooseUs: 'Pour Nous Choisir',
    trustedByCustomers: 'Approuvé par les clients dans plus de 50 pays.',
    emailDelivery: 'Livraison par Email',
    licenseKeyArrives: 'Votre clé de licence arrive directement dans votre boîte de réception, sans attente, sans expédition.',
    stripePayments: 'Paiements Stripe',
    paySafely: 'Payez en toute sécurité avec n\'importe quelle carte de crédit ou débit majeure. USD et EUR supportés.',
    genuine100: '100% Authentique',
    fullyAuthentic: 'Toutes les licences sont entièrement authentiques et viennent avec le support d\'activation officiel.',
    easyActivation: 'Activation Facile',
    simpleInstructions: 'Instructions simples étape par étape incluses avec chaque clé de licence.',
    customerSupport: 'Support Client',
    teamReadyHelp: 'Notre équipe est prête à vous aider avec l\'activation et toute question.',
    invoiceAvailable: 'Facture Disponible',
    needReceipt: 'Besoin d\'un reçu? Une facture peut être fournie sur demande.',
    browseByCategory: 'Parcourir par Catégorie',
    findRightLicense: 'Trouvez la bonne licence pour vos besoins.',
    frequentlyAskedQuestions: 'Questions Fréquemment Posées',
    readyToGetLicense: 'Prêt à obtenir votre licence?',
    instantDeliverySecurePayment: 'Livraison instantanée. Paiement sécurisé. Logiciel authentique.',
    browseLicenses: 'Parcourir les Licences',
    closeMenu: 'Fermer le Menu'
  }
}

const t = computed(() => translations[currentLang.value])

const router = useRouter()

const mobileMenuOpen = ref(false)

const reloadOverlay = ref(false)

const reloadPending = ref(false)

const navOverlay = ref(false)

onMounted(() => {
  try {
    const flag = window.sessionStorage.getItem('reload_overlay')
    if (flag === '1') {
      reloadOverlay.value = true
      window.sessionStorage.removeItem('reload_overlay')
      window.setTimeout(() => {
        reloadOverlay.value = false
      }, 900)
    }
  } catch {
    // ignore
  }
})

onMounted(() => {
  if (!import.meta.client) return
  if (!Boolean((config.public as any)?.affiliateEnabled)) return

  try {
    const ref = String((route.query as any)?.ref || '').trim()
    if (!ref) return
    const affiliateCookie = useCookie('affiliate_ref', {
      maxAge: 60 * 60 * 24 * 30,
      sameSite: 'lax',
      path: '/'
    })
    affiliateCookie.value = ref
  } catch {
    // ignore
  }
})

const search = ref('')

watch(
  () => route.query.q,
  (q) => {
    const next = String(q || '').trim()
    if (next && next !== search.value) search.value = next
  },
  { immediate: true }
)

const mainMenuBase = [
  { label: 'Windows', slug: 'windows', fallbackTo: '/categorias' },
  { label: 'Office', slug: 'office', fallbackTo: '/categorias' },
  { label: 'Windows Server', slug: 'windows-server', fallbackTo: '/categorias' },
  { label: 'Autodesk', slug: 'autodesk', fallbackTo: '/categorias' },
  { label: 'Games', slug: 'jogos', fallbackTo: '/categorias' },
  { label: 'Blog', to: '/blog' },
  { label: 'Contact', to: '/quem-somos' }
] as const

// Mapa de slug → label amigável para categorias internacionais
const INTL_SLUG_LABELS: Record<string, string> = {
  'windows': 'Windows',
  'office': 'Office',
  'windows-server': 'Windows Server',
  'software-computer-software-operating-systems': 'Operating Systems',
  'software-computer-software-office-application-software': 'Office',
  'software-video-game-software': 'Games',
  'software-computer-software-multimedia-design-software-3d-modeling-software': 'Design & 3D',
  'software-computer-software-multimedia-design-software-web-design-software': 'Web Design',
  'electronics-networking-bridges-routers': 'Routers',
  'electronics-networking-bridges-routers-wireless-routers': 'Wireless Routers',
  'electronics-electronics-accessories-computer-components-input-devices-keyboards': 'Keyboards',
}

type PaginaLinkDto = {
  titulo: string
  slug: string
}

type CategoriaLinkDto = {
  id: string
  nome: string
  slug: string
  produtoCount?: number
}

const { cart } = useCart()

const { data: siteSettings } = await useFetch('/api/site-settings', { server: true, default: () => null, key: 'site-settings' })

const { data: footerPagesData } = await useFetch('/api/paginas/footer', { server: true, default: () => null, key: 'footer-pages' })

const { data } = await useFetch<{ ok: true; paginas: PaginaLinkDto[] }>('/api/paginas', {
  server: true,
  default: () => null,
  key: 'paginas'
})

const categoriasUrl = computed(() =>
  isInternational.value ? '/api/intl/categories' : '/api/categorias'
)

const { data: categoriasData } = await useFetch<{ ok: true; categorias: CategoriaLinkDto[] }>(
  () => categoriasUrl.value,
  { server: true, default: () => null, key: 'categorias' }
)

const paginas = computed(() => data.value?.paginas || [])
const categorias = computed(() => categoriasData.value?.categorias || [])

const langPrefix = computed(() => {
  const p = String(route?.path || '')
  const langs = ['pt', 'en', 'es', 'fr', 'it', 'de']
  const first = p.split('/').filter(Boolean)[0] || ''
  if (first && langs.includes(first)) return `/${first}`
  return ''
})

const blogMenuTo = computed(() => `${langPrefix.value}/blog`)

function detectLangForAffiliateMenu() {
  const p = String(route?.path || '')
  if (p === '/en' || p.startsWith('/en/')) return 'en'
  if (p === '/es' || p.startsWith('/es/')) return 'es'
  if (p === '/pt' || p.startsWith('/pt/')) return 'pt'
  if (p === '/fr' || p.startsWith('/fr/')) return 'fr'
  if (p === '/de' || p.startsWith('/de/')) return 'de'
  return String((intl as any)?.language?.value || (intl as any)?.language || 'pt')
}

const affiliateMenuLabel = computed(() => {
  const lang = detectLangForAffiliateMenu()
  if (lang === 'en') return 'Partner Program'
  if (lang === 'es') return 'Programa de Afiliados'
  if (lang === 'fr') return "Programme d'affiliation"
  if (lang === 'de') return 'Partnerprogramm'
  return 'Programa de Afiliados'
})

const affiliateMenuTo = computed(() => {
  const lang = detectLangForAffiliateMenu()
  if (lang === 'en') return '/en/become-a-partner'
  if (lang === 'es') return '/es/programa-afiliados'
  if (lang === 'fr') return '/fr/programme-affiliation'
  if (lang === 'de') return '/de/partner-program'
  return '/en/become-a-partner'
})

const showAffiliateSeoFooterLink = computed(() => {
  return detectLangForAffiliateMenu() === 'en'
})

const categoriasSet = computed(() => {
  return new Set(categorias.value.map((c) => String(c.slug || '').trim()).filter(Boolean))
})

const productsIndexPath = computed(() => (intl.language.value === 'en' ? '/products' : '/produtos'))
const categoriesIndexPath = computed(() => (intl.language.value === 'en' ? '/categories' : '/categorias'))
const categoryPathPrefix = computed(() => (intl.language.value === 'en' ? '/category' : '/categoria'))

const aboutUsPath = computed(() => (intl.language.value === 'en' ? '/about-us' : '/quem-somos'))
const digitalDeliveryPath = computed(() => (intl.language.value === 'en' ? '/digital-delivery' : '/entrega-digital'))
const refundPolicyPath = computed(() => (intl.language.value === 'en' ? '/refund-policy' : '/reembolso'))
const privacyPolicyPath = computed(() => (intl.language.value === 'en' ? '/privacy-policy' : '/privacidade'))
const termsOfUsePath = computed(() => (intl.language.value === 'en' ? '/terms-of-use' : '/termos'))

const contactLabel = computed(() => {
  const lang = intl.language.value
  if (lang === 'en') return 'Contact'
  if (lang === 'es') return 'Contacto'
  if (lang === 'fr') return 'Contact'
  if (lang === 'it') return 'Contatti'
  return 'Contato'
})

const mainMenu = computed(() => {
  if (isInternational.value) {
    return [
      { label: 'Windows', to: '/category/windows' },
      { label: 'Office', to: '/category/office' },
      { label: 'Windows Server', to: '/category/windows-server' },
      { label: 'Adobe', to: '/category/adobe' },
      { label: 'Autodesk', to: '/category/autodesk' },
      { label: 'Games', to: '/category/games' },
      { label: 'Blog', to: '/blog' },
      { label: affiliateMenuLabel.value, to: affiliateMenuTo.value, isAffiliate: true },
      { label: 'Support', to: '/support' },
    ]
  }

  const base = mainMenuBase.map((it) => {
    if ('to' in it) {
      if (it.label === 'Contact') {
        return { label: contactLabel.value, to: aboutUsPath.value }
      }
      return it
    }
    const slug = String(it.slug || '').trim()
    const fallbackTo = categoriesIndexPath.value
    if (!slug) return { label: it.label, to: fallbackTo }
    if (categoriasSet.value.has(slug)) return { label: it.label, to: `${categoryPathPrefix.value}/${slug}` }
    return { label: it.label, to: fallbackTo }
  })

  return [...base, { label: affiliateMenuLabel.value, to: affiliateMenuTo.value, isAffiliate: true }]
})

const cartCount = computed(() => (cart.value || []).length)

type FooterPolicyLinkDto = { label: string; to: string }

const footerLinksFromPages = computed<FooterPolicyLinkDto[]>(() => {
  const pages = (footerPagesData.value as any)?.paginas
  if (!Array.isArray(pages)) return []
  return pages
    .map((p: any) => ({ label: String(p?.titulo || '').trim(), to: `/paginas/${String(p?.slug || '').trim()}` }))
    .filter((it) => it.label && it.to)
})

const footerPolicyLinksParsed = computed<FooterPolicyLinkDto[]>(() => {
  const raw = String((siteSettings.value as any)?.settings?.footerPolicyLinks || '').trim()
  if (!raw) return []

  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .map((it: any) => ({ label: String(it?.label || '').trim(), to: String(it?.to || '').trim() }))
      .filter((it) => it.label && it.to)
  } catch {
    // Fallback: one per line => "Label | /path" or "Label: /path"
    return raw
      .split(/\r?\n/g)
      .map((line) => String(line || '').trim())
      .filter(Boolean)
      .map((line) => {
        const parts = line.includes('|')
          ? line.split('|')
          : line.includes(':')
            ? line.split(':')
            : line.split(',')

        const label = String(parts[0] || '').trim()
        const toRaw = String(parts.slice(1).join(':') || '').trim()
        const to = toRaw && !toRaw.startsWith('/') ? `/${toRaw}` : toRaw
        return { label, to }
      })
      .filter((it) => it.label && it.to)
  }
})

const isIntlOrEn = computed(() => {
  // For casadosoftware.com.br, always use Portuguese regardless of other conditions
  if (normalizedHost.value.includes('casadosoftware.com.br')) return false
  return isInternational.value || intl.language.value === 'en'
})

const footerInstitutionalLinks = computed(() => {
  const lang = intl.language.value
  if (lang === 'en') {
    return [
      { label: 'About', to: aboutUsPath.value },
      { label: 'Contact', to: aboutUsPath.value },
      { label: 'Privacy', to: privacyPolicyPath.value },
      { label: 'Terms', to: termsOfUsePath.value },
      { label: 'Legal notice', to: termsOfUsePath.value }
    ]
  }
  if (lang === 'es') {
    return [
      { label: 'Sobre nosotros', to: aboutUsPath.value },
      { label: 'Contacto', to: aboutUsPath.value },
      { label: 'Privacidad', to: privacyPolicyPath.value },
      { label: 'Términos', to: termsOfUsePath.value },
      { label: 'Aviso legal', to: termsOfUsePath.value }
    ]
  }
  return [
    { label: 'Sobre', to: '/quem-somos' },
    { label: 'Contato', to: '/quem-somos' },
    { label: 'Privacidade', to: '/privacidade' },
    { label: 'Termos', to: '/termos' },
    { label: 'Aviso legal', to: '/termos' }
  ]
})

function submitSearch() {
  const q = String(search.value || '').trim()
  if (!q) {
    navigateTo(productsIndexPath.value)
    return
  }
  navigateTo({ path: productsIndexPath.value, query: { q } })
}

function onCurrencyChange(e: Event) {
  const next = String((e.target as HTMLSelectElement)?.value || '').trim().toLowerCase()
  if (next === 'brl' || next === 'usd' || next === 'eur') {
    intl.setCurrency(next)
    triggerReload()
  }
}

function onCountryChange(e: Event) {
  const next = String((e.target as HTMLSelectElement)?.value || '').trim().toUpperCase()
  if (next === 'AUTO') {
    intl.setCountry('')
    triggerReload()
    return
  }

  intl.setCountry(next)

  const eur = new Set([
    'AT',
    'BE',
    'BG',
    'HR',
    'CY',
    'CZ',
    'DK',
    'EE',
    'FI',
    'FR',
    'DE',
    'GR',
    'HU',
    'IE',
    'IT',
    'LV',
    'LT',
    'LU',
    'MT',
    'NL',
    'PL',
    'PT',
    'RO',
    'SK',
    'SI',
    'ES',
    'SE',
    'NO',
    'IS',
    'LI',
    'CH',
    'GB'
  ])

  if (next === 'BR') intl.setCurrency('brl')
  else if (eur.has(next)) intl.setCurrency('eur')
  else intl.setCurrency('usd')

  if (next === 'ES') intl.setLanguage('es')
  else if (next === 'IT') intl.setLanguage('it')
  else if (next === 'FR') intl.setLanguage('fr')
  else if (next === 'US' || next === 'GB') intl.setLanguage('en')
  else if (next === 'BR' || next === 'PT') intl.setLanguage('pt')

  triggerReload()
}

function triggerReload() {
  if (process.server) return
  if (reloadPending.value) return
  reloadPending.value = true
  try {
    window.sessionStorage.setItem('reload_overlay', '1')
  } catch {
    // ignore
  }

  window.setTimeout(() => {
    window.location.reload()
  }, 120)
}

function menuIcon(label: string) {
  const k = String(label || '').toLowerCase()
  if (k.includes('windows server')) return '🖥️'
  if (k.includes('windows')) return '🪟'
  if (k.includes('office')) return '📄'
  if (k.includes('contato')) return '☎️'
  return '•'
}

const whatsappHref = computed(() => {
  const n = String(whatsappNumber || '').replace(/\D/g, '')
  if (!n) return ''
  return `https://wa.me/${n}`
})

const whatsappLabel = computed(() => {
  const n = String(whatsappNumber || '').replace(/\D/g, '')
  if (!n) return ''
  if (n.startsWith('55') && n.length === 13) {
    const ddd = n.slice(2, 4)
    const p1 = n.slice(4, 9)
    const p2 = n.slice(9)
    return `(${ddd}) ${p1}-${p2}`
  }
  return n
})
</script>
